const { adBucketTargeting } = require('./adExperiments');
const { frontendTargeting } = require('./frontendMeasurement');
const { studyTargeting, onStudyAssignment } = require('./insights/study');
const {
  GAM_MAX_TARGETING_VALUE_LENGTH,
  normalizeTargetingValue,
  resolvePublisherProvidedId
} = require('./gamTargetingValue');

const GAM_VAST_ENDPOINT = 'https://pubads.g.doubleclick.net/gampad/ads';
const DEFAULT_VAST_SIZE = '640x480';
const GAM_MAX_TARGETING_VALUES = 30;

function normalizeTargetingValues(value, options = {}) {
  const rawValues = Array.isArray(value)
    ? value
    : options.splitCommaSeparated && typeof value === 'string'
      ? value.split(',')
      : [value];
  const seen = new Set();
  const values = [];

  rawValues.forEach((item) => {
    const unbounded = item === undefined || item === null ? '' : String(item).trim();
    if (!unbounded) return;
    const lowered = unbounded.toLowerCase();
    if (lowered === 'undefined' || lowered === 'null') return;
    if (options.filterInternal === true && /^~rmsc:/i.test(unbounded)) return;

    const normalized = normalizeTargetingValue(unbounded);
    const dedupeKey = normalized.toLowerCase();
    if (!normalized || seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    values.push(normalized);
  });

  return values.slice(0, GAM_MAX_TARGETING_VALUES);
}

function addTargetingValue(targetingMap, key, value, options = {}) {
  if (Array.isArray(value) || options.multiValue === true) {
    const normalizedValues = normalizeTargetingValues(value, options);
    if (normalizedValues.length > 0) {
      targetingMap[key] = normalizedValues;
    }
    return;
  }
  const normalized = normalizeTargetingValue(value);
  if (!normalized) {
    return;
  }
  targetingMap[key] = normalized;
}

function resolveLanguageTargetingValue({ fwjson, targeting, language }) {
  if (targeting?.useFwjsonLang) {
    const fwjsonLang = normalizeTargetingValue(fwjson?.lang);
    if (fwjsonLang) {
      return fwjsonLang;
    }
  }
  return normalizeTargetingValue(language) || 'en';
}

function resolveArticleTypeBucket(articleTypeScore) {
  const score = Number(articleTypeScore);
  if (!Number.isFinite(score)) {
    return '';
  }

  if (score <= 250) {
    return 'low';
  }

  if (score > 250 && score < 999) {
    return 'medium';
  }

  if (score >= 1000) {
    return 'high';
  }

  return '';
}

function buildArticleGamTargeting({ fwjson, targeting = {}, language = 'en' } = {}) {
  if (!fwjson) {
    return {};
  }

  const targetingMap = {};

  addTargetingValue(targetingMap, 'contentid', fwjson.id);
  addTargetingValue(targetingMap, 'category', fwjson.category);
  addTargetingValue(targetingMap, 'nsfw', fwjson.nsfw);
  addTargetingValue(targetingMap, 'keywords', fwjson.tags, {
    multiValue: true,
    splitCommaSeparated: true,
    filterInternal: true
  });
  addTargetingValue(targetingMap, 'authors', fwjson.authors, {
    multiValue: true,
    splitCommaSeparated: true
  });
  addTargetingValue(targetingMap, 'sponcon', fwjson.sponsored);
  addTargetingValue(
    targetingMap,
    'lang',
    resolveLanguageTargetingValue({ fwjson, targeting, language })
  );

  if (targeting.includeCountry) {
    addTargetingValue(targetingMap, 'country', fwjson.country);
  }

  if (targeting.includeCity) {
    addTargetingValue(targetingMap, 'city', fwjson.city);
  }

  addTargetingValue(
    targetingMap,
    'article_type',
    resolveArticleTypeBucket(fwjson.article_type)
  );

  return targetingMap;
}

function buildHomepageGamTargeting({ fwjson, targeting = {}, language = 'en' } = {}) {
  if (!fwjson) {
    return {};
  }

  const targetingMap = {};
  const category = normalizeTargetingValue(fwjson.category);
  if (category) {
    targetingMap.category = `section-${category}`;
  }

  addTargetingValue(
    targetingMap,
    'lang',
    resolveLanguageTargetingValue({ fwjson, targeting, language })
  );
  addTargetingValue(targetingMap, 'slug', fwjson.slug);
  addTargetingValue(targetingMap, 'city', fwjson.city);

  return targetingMap;
}

function buildGamTargetingConfig(targetingMap = {}) {
  const targeting = {};

  Object.entries(targetingMap).forEach(([key, value]) => {
    const normalizedKey = normalizeTargetingValue(key);
    const normalizedValues = normalizeTargetingValues(value);
    if (!normalizedKey || normalizedValues.length === 0) {
      return;
    }
    targeting[normalizedKey] = normalizedValues;
  });

  return targeting;
}

// googletag objects that already add late study assignments to their targeting.
const followingStudyAssignments = new WeakSet();

// Ads never wait for a reader's first study assignment (lib/insights/study.js).
// When it arrives, its bucket joins the page targeting, so the requests sent
// from then on (lazy-loaded slots, refreshes) carry it.
function followStudyAssignments(googletag) {
  if (followingStudyAssignments.has(googletag)) {
    return;
  }
  followingStudyAssignments.add(googletag);
  onStudyAssignment(() => {
    const targeting = buildGamTargetingConfig(studyTargeting());
    if (Object.keys(targeting).length === 0) {
      return;
    }
    const apply = () => {
      try {
        googletag.setConfig({ targeting });
      } catch (_) {
        // the next pageview carries the stored bucket
      }
    };
    if (googletag.cmd && typeof googletag.cmd.push === 'function') {
      googletag.cmd.push(apply);
    } else {
      apply();
    }
  });
}

function applyGamTargeting(googletag, targetingMap = {}) {
  if (!googletag || typeof googletag.setConfig !== 'function') {
    return;
  }

  // Every page request carries the browser's experiment bucket
  // (lib/adExperiments.js), on www.mtlblog.com the page's frontend and URL
  // band (`fe`, `pb`, lib/frontendMeasurement.js) and, for a brand lift study
  // reader, the study bucket (`nbls`, lib/insights/study.js), from the moment
  // it is known.
  followStudyAssignments(googletag);
  const targeting = buildGamTargetingConfig({
    ...targetingMap,
    ...adBucketTargeting(),
    ...frontendTargeting(),
    ...studyTargeting()
  });
  if (Object.keys(targeting).length === 0) {
    return;
  }

  googletag.setConfig({ targeting });
}

function stringifyCustParams(targetingMap = {}) {
  return Object.entries(targetingMap)
    .map(([key, value]) => {
      const normalizedKey = normalizeTargetingValue(key);
      const normalizedValues = normalizeTargetingValues(value);
      if (!normalizedKey || normalizedValues.length === 0) {
        return '';
      }
      return `${normalizedKey}=${normalizedValues.join(',')}`;
    })
    .filter(Boolean)
    .join('&');
}

function buildGamInstreamVastTag(adUnitPath, options = {}) {
  const normalizedAdUnitPath = normalizeTargetingValue(adUnitPath);
  if (!normalizedAdUnitPath) {
    return '';
  }

  const correlator = normalizeTargetingValue(options.correlator) || String(Date.now());
  const size = normalizeTargetingValue(options.size) || DEFAULT_VAST_SIZE;
  const vpmute = normalizeTargetingValue(options.vpmute) || '1';
  const vpa = normalizeTargetingValue(options.vpa) || 'auto';
  const ppid = normalizeTargetingValue(options.ppid);
  // Google video policy: declare the placement (1 = in-stream, 2 = accompanying
  // content). Undeclared or wrong values get pages restricted in Policy Center.
  const plcmt = normalizeTargetingValue(options.plcmt);
  const descriptionUrl = typeof options.descriptionUrl === 'string' ? options.descriptionUrl.trim() : '';

  const params = new URLSearchParams({
    iu: normalizedAdUnitPath,
    sz: size,
    gdfp_req: '1',
    output: 'vast',
    unviewed_position_start: '1',
    env: 'vp',
    vpmute,
    vpa,
    impl: 's',
    correlator
  });

  if (plcmt) {
    params.set('plcmt', plcmt);
  }

  if (descriptionUrl) {
    params.set('description_url', descriptionUrl);
  }

  const custParams = stringifyCustParams({
    ...(options.targeting || {}),
    ...adBucketTargeting(),
    ...frontendTargeting(),
    ...studyTargeting()
  });
  if (custParams) {
    params.set('cust_params', custParams);
  }

  if (ppid) {
    params.set('ppid', ppid);
  }

  return `${GAM_VAST_ENDPOINT}?${params.toString()}`;
}

module.exports = {
  GAM_MAX_TARGETING_VALUE_LENGTH,
  GAM_MAX_TARGETING_VALUES,
  buildArticleGamTargeting,
  buildHomepageGamTargeting,
  applyGamTargeting,
  buildGamInstreamVastTag,
  normalizeTargetingValue,
  normalizeTargetingValues,
  resolvePublisherProvidedId
};
